import { useEffect, useRef, type ReactNode } from "react";
import type {
  WorkToastQuestion,
  WorkToastQuestionOption,
} from "@/stores/work-toast-store";
import { useT } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { WorkToastQuestionOptions } from "./work-toast-question-options";

function isTypingTarget(target: EventTarget | null): boolean {
  // A keydown on `document` has `document` as its target, which has no
  // `closest` — guard on HTMLElement or the handler throws and eats the key.
  return (
    target instanceof HTMLElement &&
    Boolean(target.closest("input, textarea, [contenteditable='true']"))
  );
}

export function WorkToastQuestionBody({
  question,
  disabled,
  onPickOption,
  reply,
}: {
  question: WorkToastQuestion;
  disabled: boolean;
  onPickOption: (option: WorkToastQuestionOption) => void;
  reply: ReactNode;
}) {
  const t = useT(toastsMessages);
  const containerRef = useRef<HTMLDivElement>(null);
  const latest = useRef({ question, disabled, onPickOption });
  latest.current = { question, disabled, onPickOption };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        isTypingTarget(event.target)
      )
        return;
      if (!/^[1-9]$/.test(event.key)) return;
      // Scope to whichever card currently holds focus (same rule as the
      // card's Esc handler) so a digit key doesn't act on every mounted
      // question body, and doesn't fire from an unrelated keypress made
      // while focus sits on `document.body` (e.g. after clicking the
      // canvas) or on any element outside this card. `ToastCard` already
      // focuses the first body control (or its own root) on mount, so in
      // production the focus claim exists by the time a user can press a
      // digit — `document.body` is never treated as eligible here.
      if (!containerRef.current?.contains(document.activeElement)) return;
      const {
        question: current,
        disabled: blocked,
        onPickOption: pick,
      } = latest.current;
      const option = current.options[Number(event.key) - 1];
      if (!option || blocked) return;
      event.preventDefault();
      pick(option);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const { index, count } = question;
  const hasStep = typeof index === "number" && typeof count === "number";
  return (
    // A normal block element (not `display: contents`) so focus containment
    // is reliable on WKWebView (the desktop app's renderer) — `contains()`
    // checks on a `display: contents` node have shown inconsistent behavior
    // there. `flex flex-col gap-2` reproduces the parent body wrapper's own
    // spacing (ToastCard's `flex flex-col gap-2 px-1`) so nesting one level
    // deeper doesn't change the rendered layout.
    <div ref={containerRef} className="flex flex-col gap-2">
      {hasStep && (
        <span
          className="text-appkit-caption font-semibold uppercase tracking-[0.08em] text-muted"
          data-testid="work-toast-question-step"
        >
          {t("questionStep", { index, count })}
        </span>
      )}
      <p className="text-appkit-body text-primary">{question.question}</p>
      {question.context && (
        <p className="text-appkit-small font-medium text-muted">
          {question.context}
        </p>
      )}
      <WorkToastQuestionOptions
        options={question.options}
        disabled={disabled}
        onPickOption={onPickOption}
      />
      {reply}
    </div>
  );
}
